import { IChannel, IPoll, ITextFieldCollection } from "@Interfaces";
import { CalcExpiryDate } from "@Utils/calcExpiryDate";
import { filterActiveChannels } from "@Utils/channelFilters";
import { resolveRedirectTarget } from "@Utils/redirectUrl";
import { hasVotedOnPoll } from "./channelCompletion.utils";

/**
 * Where a participant stands relative to the channel's completion redirect.
 * - disabled: no redirect configured, a channel admin is viewing, or the parent lobby
 *   still has published sub-channels to pick from.
 * - pending: text fields are still loading, so completion cannot be judged yet.
 * - empty: nothing to answer (closed event, no components, or every sub-channel
 *   unpublished). Unpublished events never load; the share-link thunk handles those.
 * - incomplete: something is still left to answer.
 * - pollsDone: every poll is answered but at least one text field was skipped.
 * - complete: every poll and text field is answered.
 */
export type CompletionRedirectStatus =
    | "disabled"
    | "pending"
    | "empty"
    | "incomplete"
    | "pollsDone"
    | "complete";

type GetCompletionRedirectStatusParams = {
    channel: IChannel | null | undefined;
    /** `null` while the channel's text fields are loading or failed to load. */
    textFields: ITextFieldCollection[] | null;
    submittedTextFieldIds: string[];
    /**
     * Polls this browser voted on in earlier visits. The share-link API never returns the
     * visitor's own votes, so returning participants are only recognisable from local storage.
     */
    previouslyVotedPollIds?: string[];
    /** Text fields this browser answered in earlier visits, for the same reason. */
    previouslySubmittedTextFieldIds?: string[];
    isChannelAdmin: boolean;
    now?: number;
};

/**
 * Returns the redirect URL when the channel has the redirect switched on with a URL
 * participants may be sent to.
 */
export const getCompletionRedirectUrl = (
    channel: IChannel | null | undefined
): string | null => {
    const settings = channel?.settings;
    return settings?.completion_redirect_enabled
        ? resolveRedirectTarget(settings.completion_redirect_url)
        : null;
};

// Closed or expired polls cannot be voted on, so they must not block completion.
const isPollAnswerable = (
    poll: IPoll,
    votingTimeLimit: number,
    now: number
): boolean => {
    if (poll.active === false) {
        return false;
    }

    if (!votingTimeLimit) {
        return true;
    }

    return CalcExpiryDate(votingTimeLimit, poll.published).valueOf() > now;
};

/**
 * When the next poll the participant hasn't answered stops taking votes, so completion can be
 * judged again then (an expired poll no longer blocks it). Null when none is due to expire.
 */
export const getNextUnansweredPollExpiry = ({
    channel,
    previouslyVotedPollIds = [],
    now = Date.now(),
}: {
    channel: IChannel | null | undefined;
    previouslyVotedPollIds?: string[];
    now?: number;
}): number | null => {
    const votingTimeLimit = channel?.settings?.voting_time_limit ?? 0;
    if (!votingTimeLimit) {
        return null;
    }

    const previouslyVoted = new Set(previouslyVotedPollIds);
    const expiries = (channel?.polls ?? [])
        .filter(
            (poll) =>
                isPollAnswerable(poll, votingTimeLimit, now) &&
                !hasVotedOnPoll(poll) &&
                !previouslyVoted.has(poll._id)
        )
        .map((poll) =>
            CalcExpiryDate(votingTimeLimit, poll.published).valueOf()
        );

    return expiries.length > 0 ? Math.min(...expiries) : null;
};

/**
 * Pure decision for the completion redirect; the hook decides when to act on it.
 */
export const getCompletionRedirectStatus = ({
    channel,
    textFields,
    submittedTextFieldIds,
    previouslyVotedPollIds = [],
    previouslySubmittedTextFieldIds = [],
    isChannelAdmin,
    now = Date.now(),
}: GetCompletionRedirectStatusParams): CompletionRedirectStatus => {
    if (!channel?._id || !getCompletionRedirectUrl(channel) || isChannelAdmin) {
        return "disabled";
    }

    // A closed event can't be voted on any more, whatever it contains.
    if (channel.closed) {
        return "empty";
    }

    if (channel.has_children) {
        const publishedSubChannels = filterActiveChannels(
            channel.sub_channels ?? []
        );
        // Published sub-channels: the lobby runs as usual and the chosen
        // sub-channel (which inherits these settings) is evaluated instead.
        return publishedSubChannels.length > 0 ? "disabled" : "empty";
    }

    if (textFields === null) {
        return "pending";
    }

    const polls = channel.polls ?? [];
    if (polls.length === 0 && textFields.length === 0) {
        return "empty";
    }

    const previouslyVoted = new Set(previouslyVotedPollIds);
    const isVoted = (poll: IPoll) =>
        hasVotedOnPoll(poll) || previouslyVoted.has(poll._id);
    const votingTimeLimit = channel.settings?.voting_time_limit ?? 0;
    const answerablePolls = polls.filter((poll) =>
        isPollAnswerable(poll, votingTimeLimit, now)
    );
    const pollsDone = answerablePolls.every(isVoted);
    const submitted = new Set([
        ...submittedTextFieldIds,
        ...previouslySubmittedTextFieldIds,
    ]);
    const answeredTextFields = textFields.filter((textField) =>
        submitted.has(textField._id)
    ).length;

    if (pollsDone && answeredTextFields === textFields.length) {
        return "complete";
    }

    // Offer the continue link only once the participant has actually answered something,
    // so a text-field-only channel doesn't show it before any input.
    const answeredAnything = answeredTextFields > 0 || polls.some(isVoted);

    return pollsDone && answeredAnything ? "pollsDone" : "incomplete";
};
